import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Headphones, 
  Mail, 
  MessageSquare, 
  FileText, 
  HelpCircle,
  Zap,
  Wrench,
  DollarSign,
  Smartphone,
  Shield,
  Clock,
  Send,
  Loader2,
  CheckCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  BookOpen,
  AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SupportPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    category: 'general',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        name: formData.name,
        email: formData.email,
        subject: `[${formData.category.toUpperCase()}] ${formData.subject}`,
        message: formData.message,
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', category: 'general', message: '' });
    } catch (error) {
      console.error('Support form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const supportCategories = [
    { id: 'general', label: 'General Inquiry', icon: HelpCircle },
    { id: 'technical', label: 'Technical Issue', icon: Wrench },
    { id: 'billing', label: 'Billing & Subscription', icon: DollarSign },
    { id: 'feature', label: 'Feature Request', icon: Zap },
    { id: 'bug', label: 'Bug Report', icon: AlertCircle },
    { id: 'account', label: 'Account & Privacy', icon: Shield },
  ];

  const quickHelp = [
    {
      icon: Smartphone,
      title: 'Getting Started',
      description: 'New to 2A Valuence? Learn how to set up your account and start pricing devices.',
      links: [
        { text: 'Download the App', url: 'https://apps.apple.com/app/id6759179016' },
      ]
    },
    {
      icon: DollarSign,
      title: 'Pricing Questions',
      description: 'Understand how our pricing algorithms work and how to get accurate estimates.',
      links: [
        { text: 'View Features', url: '/#features' },
      ]
    },
    {
      icon: Wrench,
      title: 'Repair Hub Help',
      description: 'Learn how to manage repair tickets, assign technicians, and track warranties.',
      links: [
        { text: 'Repair Hub Overview', url: '/#repair-hub' },
      ]
    },
    {
      icon: Shield,
      title: 'Privacy & Data',
      description: 'Questions about your data, privacy rights, or account security.',
      links: [
        { text: 'Privacy Policy', url: '/privacy' },
        { text: 'Terms of Service', url: '/terms' },
      ]
    },
  ];

  const faqs = [
    {
      question: 'How do I reset my password?',
      answer: 'You can reset your password through the App by tapping "Forgot Password" on the login screen. Enter your email address, and we\'ll send you a password reset link. If you don\'t receive the email within a few minutes, check your spam folder or contact support.',
    },
    {
      question: 'Why is my pricing estimate different from actual market prices?',
      answer: 'Our pricing estimates are based on analysis of recent sold listings and market data. Actual prices can vary based on device condition, local market demand, timing, and buyer preferences. We recommend using our estimates as a reference guide and verifying with current listings before making final pricing decisions. See our Terms of Service for full disclaimer.',
    },
    {
      question: 'How do I cancel my subscription?',
      answer: 'For iOS subscriptions, go to Settings > Apple ID > Subscriptions on your iPhone/iPad and select 2A Valuence to cancel. Cancellation takes effect at the end of your current billing period. You\'ll retain access to premium features until then.',
    },
    {
      question: 'Can I export my customer and repair data?',
      answer: 'Yes, you can export your data from the App\'s settings menu. Go to Settings > Data Management > Export Data. You can export customer lists, repair history, and pricing searches in CSV format. For complete data portability requests, contact us at 2avaluence@gmail.com.',
    },
    {
      question: 'How do I connect my Gmail or Outlook for email notifications?',
      answer: 'In the App, go to Settings > Email Integration and select your email provider. You\'ll be redirected to sign in with your Google or Microsoft account and grant permission for the App to send emails on your behalf. We only request the minimum permissions necessary.',
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes, we take security seriously. All data is encrypted in transit and at rest. We use industry-standard security practices and never sell your personal information. See our Privacy Policy for complete details on how we protect your data.',
    },
    {
      question: 'How do I delete my account?',
      answer: 'To delete your account, go to Settings > Account > Delete Account in the App, or email us at 2avaluence@gmail.com with the subject "Account Deletion Request." We\'ll process your request within 30 days and confirm deletion via email.',
    },
    {
      question: 'What devices are supported for pricing?',
      answer: 'We support pricing for a wide range of electronics including iPhones, Samsung Galaxy phones, Google Pixel, iPads, MacBooks, PlayStation, Xbox, Nintendo Switch, and many more. If a device is commonly sold on eBay and other resale platforms, we can likely provide pricing data for it.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0F0F1A]" data-testid="support-page">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-glass border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
            data-testid="support-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Hero */}
          <div className="text-center mb-12 sm:mb-16">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-6">
              <Headphones className="w-8 h-8 sm:w-10 sm:h-10 text-primary" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white font-['Outfit'] mb-4">
              How Can We Help?
            </h1>
            <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
              Get support for 2A Valuence. Browse our help resources or contact our team directly.
            </p>
          </div>

          {/* Quick Contact Banner */}
          <div className="glass-card p-4 sm:p-6 mb-12 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3 text-center sm:text-left">
              <Mail className="w-6 h-6 text-primary flex-shrink-0" />
              <div>
                <p className="text-white font-medium">Email Support</p>
                <p className="text-slate-400 text-sm">We typically respond within 24-48 hours</p>
              </div>
            </div>
            <a 
              href="mailto:2avaluence@gmail.com" 
              className="btn-primary flex items-center gap-2 px-6 py-3"
              data-testid="email-support-btn"
            >
              <Mail className="w-4 h-4" />
              2avaluence@gmail.com
            </a>
          </div>

          {/* Quick Help Cards */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-primary" />
              Quick Help
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {quickHelp.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="glass-card p-5 sm:p-6 hover:border-primary/30 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-slate-400 text-sm mb-4">{item.description}</p>
                  <div className="space-y-2">
                    {item.links.map((link, i) => (
                      <Link 
                        key={i}
                        to={link.url}
                        className="text-primary text-sm hover:underline flex items-center gap-1"
                      >
                        {link.text}
                        <ExternalLink className="w-3 h-3" />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-primary" />
              Frequently Asked Questions
            </h2>
            <div className="glass-card divide-y divide-white/10">
              {faqs.map((faq, index) => (
                <div key={index} className="p-4 sm:p-5">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-start justify-between gap-4 text-left"
                    data-testid={`faq-${index}`}
                  >
                    <span className="text-white font-medium text-sm sm:text-base">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                    )}
                  </button>
                  {openFaq === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      className="mt-3 text-slate-400 text-sm leading-relaxed"
                    >
                      {faq.answer}
                    </motion.div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <MessageSquare className="w-6 h-6 text-primary" />
              Contact Support
            </h2>
            
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Form */}
              <div className="lg:col-span-2">
                <div className="glass-card p-5 sm:p-8">
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="support-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label className="text-slate-300 text-sm mb-2 block">Your Name *</label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="John Smith"
                          className="bg-white/5 border-white/10 focus:border-primary h-11 text-white placeholder:text-slate-500"
                          data-testid="support-name-input"
                        />
                      </div>
                      <div>
                        <label className="text-slate-300 text-sm mb-2 block">Email Address *</label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="john@example.com"
                          className="bg-white/5 border-white/10 focus:border-primary h-11 text-white placeholder:text-slate-500"
                          data-testid="support-email-input"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="text-slate-300 text-sm mb-2 block">Category *</label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {supportCategories.map((cat) => (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => setFormData({ ...formData, category: cat.id })}
                            className={`p-3 rounded-lg border text-left transition-all flex items-center gap-2 text-sm ${
                              formData.category === cat.id
                                ? 'border-primary bg-primary/10 text-white'
                                : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20'
                            }`}
                            data-testid={`category-${cat.id}`}
                          >
                            <cat.icon className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{cat.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-slate-300 text-sm mb-2 block">Subject *</label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="Brief description of your issue"
                        className="bg-white/5 border-white/10 focus:border-primary h-11 text-white placeholder:text-slate-500"
                        data-testid="support-subject-input"
                      />
                    </div>

                    <div>
                      <label className="text-slate-300 text-sm mb-2 block">Message *</label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Please describe your issue or question in detail. Include any relevant information like device model, app version, or error messages."
                        rows={5}
                        className="bg-white/5 border-white/10 focus:border-primary text-white placeholder:text-slate-500 resize-none"
                        data-testid="support-message-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full sm:w-auto flex items-center justify-center gap-2 py-5"
                      data-testid="support-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5" />
                          Send Message
                        </>
                      )}
                    </Button>

                    {submitStatus === 'success' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 text-[#10B981] bg-[#10B981]/10 p-4 rounded-lg"
                        data-testid="support-success-message"
                      >
                        <CheckCircle className="w-5 h-5 flex-shrink-0" />
                        <span>Message sent successfully! We'll get back to you within 24-48 hours.</span>
                      </motion.div>
                    )}

                    {submitStatus === 'error' && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-400 bg-red-500/10 p-4 rounded-lg"
                        data-testid="support-error-message"
                      >
                        Something went wrong. Please try again or email us directly at 2avaluence@gmail.com
                      </motion.div>
                    )}
                  </form>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Response Time */}
                <div className="glass-card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-5 h-5 text-primary" />
                    <h3 className="text-white font-semibold">Response Time</h3>
                  </div>
                  <p className="text-slate-400 text-sm">
                    We typically respond to support requests within <strong className="text-white">24-48 business hours</strong>.
                    Urgent issues are prioritized.
                  </p>
                </div>

                {/* Direct Email */}
                <div className="glass-card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <Mail className="w-5 h-5 text-primary" />
                    <h3 className="text-white font-semibold">Email Directly</h3>
                  </div>
                  <p className="text-slate-400 text-sm mb-3">
                    Prefer email? Reach us directly at:
                  </p>
                  <a 
                    href="mailto:2avaluence@gmail.com"
                    className="text-primary hover:underline text-sm font-medium"
                  >
                    2avaluence@gmail.com
                  </a>
                </div>

                {/* App Info */}
                <div className="glass-card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <Smartphone className="w-5 h-5 text-primary" />
                    <h3 className="text-white font-semibold">App Information</h3>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-slate-400">
                      <span className="text-slate-300">App Name:</span> 2A Valuence
                    </p>
                    <p className="text-slate-400">
                      <span className="text-slate-300">Version:</span> 1.2.0
                    </p>
                    <p className="text-slate-400">
                      <span className="text-slate-300">Platform:</span> iOS
                    </p>
                    <a 
                      href="https://apps.apple.com/app/id6759179016"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline mt-2"
                    >
                      View on App Store
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>

                {/* Legal Links */}
                <div className="glass-card p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <FileText className="w-5 h-5 text-primary" />
                    <h3 className="text-white font-semibold">Legal</h3>
                  </div>
                  <div className="space-y-2">
                    <Link to="/privacy" className="block text-slate-400 hover:text-white text-sm transition-colors">
                      Privacy Policy
                    </Link>
                    <Link to="/terms" className="block text-slate-400 hover:text-white text-sm transition-colors">
                      Terms of Service
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="mt-16 pt-8 border-t border-white/10 text-center">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} 2A Valuence. All rights reserved.
            </p>
            <div className="flex justify-center gap-6 mt-4">
              <Link to="/privacy" className="text-primary hover:underline text-sm">Privacy Policy</Link>
              <Link to="/terms" className="text-primary hover:underline text-sm">Terms of Service</Link>
              <Link to="/" className="text-primary hover:underline text-sm">Home</Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SupportPage;
